/**
 * Employee Filters Component
 * Search and filter controls for employee list
 */
import React from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Filter } from 'lucide-react';

export const EmployeeFilters = ({
  filters,
  setFilters,
  departments,
  showFilters,
  setShowFilters,
  onSearch,
  onFilterChange,
  onClearFilters,
  activeTab
}) => {
  return (
    <div className="bg-white rounded-xl border p-4">
      <form onSubmit={onSearch} className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search by name, code, email, mobile, login ID..."
            value={filters.search}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        <Button type="submit" variant="secondary" data-testid="search-btn">
          Search
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => setShowFilters(!showFilters)}
          data-testid="filter-toggle"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </Button>
      </form>

      {showFilters && (
        <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-4 gap-4">
          <Select value={filters.department_id || '__all__'} onValueChange={(v) => onFilterChange('department_id', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid="filter-department">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={filters.status || '__all__'} 
            onValueChange={(v) => onFilterChange('status', v === '__all__' ? '' : v)}
            disabled={activeTab === 'active'}
          >
            <SelectTrigger data-testid="filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Status</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>

          <Select 
            value={filters.authorization_status || '__all__'} 
            onValueChange={(v) => onFilterChange('authorization_status', v === '__all__' ? '' : v)}
            disabled={activeTab === 'pending_auth'}
          >
            <SelectTrigger data-testid="filter-auth-status">
              <SelectValue placeholder="Auth Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Auth Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="authorized">Authorized</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filters.employment_type || '__all__'} onValueChange={(v) => onFilterChange('employment_type', v === '__all__' ? '' : v)}>
            <SelectTrigger data-testid="filter-employment-type">
              <SelectValue placeholder="Employment Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All Types</SelectItem>
              <SelectItem value="permanent">Permanent</SelectItem>
              <SelectItem value="contractual">Contractual</SelectItem>
              <SelectItem value="trainee">Trainee</SelectItem>
              <SelectItem value="probation">Probation</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" onClick={onClearFilters} data-testid="clear-filters">
            Clear Filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default EmployeeFilters;
